import { useCallback, useMemo, useEffect, useRef, useReducer, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { useQuery, useApolloClient } from '@apollo/client';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { v7 as uuidv7 } from 'uuid';
import { CHAT_PARTS_DEFAULT } from '@arthelper/shared/chat-parts';
import { CHAT_META_QUERY, CHAT_QUERY, CHAT_TITLE_QUERY } from '~/graphql/queries';
import { useAuth } from '~/context/auth';
import { useAnalytics } from '~/context/analytics';
import { chatHistory } from '~/utils/chatHistory';
import { useApiClient } from '~/utils/apiClient';
import { captureSparklinkUploadModalParams } from '~/utils/sparklinkParams';
import { applyStableFilePartUrls, type ChatFilePart } from '~/components/Chat/utils/fileParts';

interface ChatSubmitOptions {
  input?: string;
  body?: object;
  allowEmptySubmit?: boolean;
  files?: ChatFilePart[];
}

function buildChatUrl(basePath: string, chatId?: string): string {
  if (!chatId) return basePath;

  if (basePath.includes('?collectionId=')) {
    const collectionId = basePath.split('?collectionId=')[1];
    return `/art/chat/${chatId}?collectionId=${collectionId}`;
  }

  if (basePath.includes('?seriesId=')) {
    const seriesId = basePath.split('?seriesId=')[1];
    return `/art/chat/${chatId}?seriesId=${seriesId}`;
  }

  if (basePath.startsWith('/art/') && basePath.endsWith('/chat')) {
    return `${basePath}/${chatId}`;
  }

  return `${basePath}/${chatId}`;
}

function createMessage(part: any): any {
  return {
    id: part.id,
    role: part.role,
    content: '',
    createdAt: new Date(part.createdAt),
    parts: [],
    good: [true, false].includes(part.good) ? part.good : null,
  };
}

function addTextPart(message: any, part: any): void {
  message.parts.push({
    type: 'text',
    text: part.content,
    id: part.id,
    good: part.good,
    notes: part.notes,
  });

  message.content += (message.content ? '\n' : '') + part.content;

  // The spark and piece a user turn ran under, so regenerating a resumed chat reruns them.
  if (message.role === 'user' && part.metadata) {
    message.metadata = part.metadata;
  }
}

function addAttachmentPart(message: any, part: any): void {
  if (!message.experimental_attachments) {
    message.experimental_attachments = [];
  }
  message.experimental_attachments.push(part.attachment);
}

function addToolPart(message: any, part: any): void {
  message.parts.push({
    type: 'tool-invocation',
    toolInvocation: part.toolCall,
    id: part.id,
  });
}

function processPart(message: any, part: any): void {
  if (part.type === 'text' && part.content) {
    addTextPart(message, part);
    return;
  }

  if (part.type === 'attachment' && part.attachment) {
    addAttachmentPart(message, part);
    return;
  }

  if (part.type === 'tool_invocation' && part.toolCall) {
    addToolPart(message, part);
  }
}

function partsToMessages(chatParts: any[]): any[] {
  if (!chatParts?.length) return [];

  const messages: any[] = [];
  let currentMessage: any = null;

  for (const part of chatParts) {
    if (!currentMessage || currentMessage.role !== part.role) {
      if (currentMessage) {
        messages.push(currentMessage);
      }
      currentMessage = createMessage(part);
    }

    processPart(currentMessage, part);
  }

  if (currentMessage) {
    messages.push(currentMessage);
  }

  return messages;
}

function mergeMessages(earlier: any, later: any): any {
  const attachments = [...(earlier.experimental_attachments ?? []), ...(later.experimental_attachments ?? [])];

  return {
    ...earlier,
    content: [earlier.content, later.content].filter(Boolean).join('\n'),
    parts: [...(earlier.parts ?? []), ...(later.parts ?? [])],
    ...(attachments.length && { experimental_attachments: attachments }),
    ...((later.metadata ?? earlier.metadata) && { metadata: later.metadata ?? earlier.metadata }),
  };
}

// A page boundary can split one turn; consecutive same-role messages are one message, as in partsToMessages.
function prependMessages(older: any[], current: any[]): any[] {
  const last = older[older.length - 1];
  const first = current[0];
  if (!last || !first || last.role !== first.role) {
    return [...older, ...current];
  }

  return [...older.slice(0, -1), mergeMessages(last, first), ...current.slice(1)];
}

/**
 * The loaded transcript window of one opened chat, captured from its FIRST network result and
 * never from a cache hit. `keyLength` freezes the useChat key; `partCount` is the server count at
 * that moment, so parts sent this session (only in useChat state) never read as "earlier".
 */
type PartsWindow = {
  chatId: string;
  keyLength: number;
  partCount: number;
  loaded: number;
  earliest?: number;
  exhausted: boolean;
  loading: boolean;
};

function lowestPartIndex(parts: any[]): number | undefined {
  return parts.reduce((memo: number | undefined, part: any) => {
    if (memo === undefined || part.partIndex < memo) {
      return part.partIndex;
    }
    return memo;
  }, undefined);
}

function windowFrom(chat: any): PartsWindow {
  const parts = chat.parts ?? [];

  return {
    chatId: chat.id,
    keyLength: parts.length,
    partCount: chat.partCount ?? parts.length,
    loaded: parts.length,
    earliest: lowestPartIndex(parts),
    exhausted: false,
    loading: false,
  };
}

function hasEarlier(transcript: PartsWindow | null): boolean {
  if (!transcript || transcript.exhausted || transcript.earliest === undefined) {
    return false;
  }

  return transcript.partCount > transcript.loaded;
}

function keyLength(isStarted: boolean, transcript: PartsWindow | null, fetchedChat: any): number {
  if (isStarted) {
    return 0;
  }

  if (transcript) {
    return transcript.keyLength;
  }

  return fetchedChat?.parts?.length ?? 0;
}

// Simple reducer for deep link state
function deepLinkReducer(state: any, updates: any) {
  return { ...state, ...updates };
}

/** The spark and piece one user turn runs under. Rides on that message's metadata, never on the hook. */
export type ChatTurn = { sparkId?: string; imageId?: string };

/** What a standalone chat opens under: its first user turn runs `sparkId`/`imageId`, every turn `context`. */
export type ChatOpening = ChatTurn & { context?: string };

type StandaloneChat = { id: string; started: boolean; resumed: boolean };

function freshStandaloneChat(): StandaloneChat {
  return { id: uuidv7(), started: false, resumed: false };
}

function activeConversationId(standalone: boolean, standaloneChat: StandaloneChat, routeId?: string) {
  if (!standalone) {
    return routeId;
  }

  if (!standaloneChat.started) {
    return undefined;
  }

  return standaloneChat.id;
}

function openingTurn(messages: any[], opening: ChatOpening): ChatTurn {
  const userTurns = messages.filter((message) => message.role === 'user').length;
  if (userTurns !== 1) {
    return {};
  }

  return { sparkId: opening.sparkId, imageId: opening.imageId };
}

// A regenerate re-sends the same user message, so it reruns the same spark on the same piece.
function standaloneTurnBody(messages: any[], opening: ChatOpening): Record<string, string> {
  const turn = (messages[messages.length - 1]?.metadata ?? {}) as ChatTurn;
  const fallback = openingTurn(messages, opening);
  const values: Record<string, string | undefined> = {
    sparkId: turn.sparkId ?? fallback.sparkId,
    imageId: turn.imageId ?? fallback.imageId,
    context: opening.context,
  };

  return Object.entries(values).reduce((memo: Record<string, string>, [key, value]) => {
    if (value) {
      memo[key] = value;
    }
    return memo;
  }, {});
}

function turnMetadata(turn?: ChatTurn): ChatTurn | undefined {
  if (!turn?.sparkId && !turn?.imageId) {
    return undefined;
  }

  return { ...(turn.sparkId && { sparkId: turn.sparkId }), ...(turn.imageId && { imageId: turn.imageId }) };
}

export function useChatHook({
  targetType,
  targetId,
  userId,
  model,
  basePath = '/chat',
  standalone = false,
  opening,
  enabled = true,
}: {
  targetType?: string;
  targetId?: string;
  userId: string;
  model: string;
  basePath?: string;
  /** Detached from the router: never reads the page URL, never navigates, owns its own chat id. */
  standalone?: boolean;
  /** Standalone only: the spark, piece and context the chat's opening turn runs under. */
  opening?: ChatOpening;
  /** False keeps this instance from loading any chat (a layout that only renders it on some routes). */
  enabled?: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const apolloClient = useApolloClient();
  const { amplitude, intercom } = useAnalytics();
  const apiClient = useApiClient();
  const { conversationId: routeConversationId } = useParams<{ conversationId?: string }>();
  const [standaloneChat, setStandaloneChat] = useState<StandaloneChat>(freshStandaloneChat);
  const conversationId = activeConversationId(standalone, standaloneChat, routeConversationId);
  const openingRef = useRef<ChatOpening>(opening ?? {});
  openingRef.current = opening ?? {};

  // The chat this hook instance started (first send, or a fresh id it navigated to with a pending
  // message). Its parts live only in useChat state, so CHAT_QUERY never loads it mid-stream.
  const startedChatIdRef = useRef<string | undefined>(undefined);
  const isStarted = !!conversationId && conversationId === startedChatIdRef.current;

  useEffect(() => {
    if (conversationId !== startedChatIdRef.current) {
      startedChatIdRef.current = undefined;
    }
  }, [conversationId]);

  // Track if we've processed spark link params to prevent clearing them prematurely
  const hasProcessedSparkLinkRef = useRef(false);

  // Deep link state
  const [deepLink, setDeepLink] = useReducer(deepLinkReducer, {
    sparkId: localStorage.getItem('pendingSparkId'),
    trigger: 0,
  });

  // Handle URL parameters
  useEffect(() => {
    if (standalone) return;

    const messageParam = searchParams.get('message');
    const sparkIdParam = searchParams.get('spark_id');
    const displayUploadModal = searchParams.get('display_upload_modal') === 'true';

    // Only handle URL params if we're on a route that matches this hook's basePath
    const location = window.location;
    const isCurrentRoute = location.pathname === basePath || location.pathname.startsWith(`${basePath}/`);
    if (!isCurrentRoute) return;

    captureSparklinkUploadModalParams(searchParams);

    // Clear sparkId only if no spark_id parameter is present AND we haven't just processed a spark link
    // Don't clear if we just removed it from URL - only clear if user navigated to a non-spark link conversation
    if (!sparkIdParam && !hasProcessedSparkLinkRef.current) {
      const storedSparkId = localStorage.getItem('pendingSparkId');
      const storedChatId = localStorage.getItem('pendingChatId');

      // Only clear if we have a stored sparkId but it's for a different conversation
      if (storedSparkId && conversationId && storedChatId && storedChatId !== conversationId) {
        // Different conversation, safe to clear
        localStorage.removeItem('pendingSparkId');
        setDeepLink({ sparkId: null });
      }
    }

    // If no parameters are present, return early
    if (!messageParam && !sparkIdParam) {
      return;
    }

    // If upload modal is enabled, delay storing pending message until modal closes
    // Store it in a different key that won't trigger chat processing
    if (displayUploadModal && messageParam) {
      localStorage.setItem('pendingMessageDelayed', messageParam);
      // Don't store in pendingMessage yet - wait for modal to close
    }

    if (!conversationId) {
      // New conversation
      const newChatId = uuidv7();
      if (messageParam && !displayUploadModal) {
        // Only store immediately if upload modal is not enabled
        localStorage.setItem('pendingMessage', messageParam);
        localStorage.setItem('pendingChatId', newChatId);
      } else if (messageParam && displayUploadModal) {
        // Store chatId for delayed message (pendingMessageDelayed already set above)
        localStorage.setItem('pendingChatId', newChatId);
      }
      startedChatIdRef.current = newChatId;
      if (sparkIdParam) {
        localStorage.setItem('pendingSparkId', sparkIdParam);
        setDeepLink({ sparkId: sparkIdParam });
        hasProcessedSparkLinkRef.current = true;
      }
      // Remove message and spark link params (they're captured/stored, no longer needed in URL)
      const newSearchParams = new URLSearchParams(searchParams);
      if (messageParam) newSearchParams.delete('message');
      // Remove spark link params (upload-modal config already in sessionStorage via captureSparklinkUploadModalParams)
      const sparkLinkParams = [
        'spark_id',
        'display_upload_modal',
        'force_uploader_on_all_users',
        'force_uploader_when_zero_uploads_only',
        'modal_header_text',
        'modal_header_subtext',
      ];
      sparkLinkParams.forEach((param) => {
        newSearchParams.delete(param);
      });
      const newSearch = newSearchParams.toString();
      navigate(`${basePath}/${newChatId}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
      return;
    }

    // Existing conversation
    if (messageParam && !displayUploadModal) {
      // Only store immediately if upload modal is not enabled
      localStorage.setItem('pendingMessage', messageParam);
      localStorage.setItem('pendingChatId', conversationId);
      setDeepLink({ trigger: Date.now() });
    } else if (messageParam && displayUploadModal) {
      // Store chatId for delayed message (pendingMessageDelayed already set above)
      localStorage.setItem('pendingChatId', conversationId);
    }
    if (sparkIdParam) {
      localStorage.setItem('pendingSparkId', sparkIdParam);
      setDeepLink({ sparkId: sparkIdParam });
      hasProcessedSparkLinkRef.current = true;
    }

    // Remove message and spark link params (they're captured/stored, no longer needed in URL)
    const newSearchParams = new URLSearchParams(searchParams);
    if (messageParam) newSearchParams.delete('message');
    // Remove spark link params (upload-modal config already in sessionStorage via captureSparklinkUploadModalParams)
    const sparkLinkParams = [
      'spark_id',
      'display_upload_modal',
      'force_uploader_on_all_users',
      'force_uploader_when_zero_uploads_only',
      'modal_header_text',
      'modal_header_subtext',
    ];
    sparkLinkParams.forEach((param) => {
      newSearchParams.delete(param);
    });
    const newSearch = newSearchParams.toString();
    navigate(`${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  }, [searchParams, conversationId, navigate, basePath, standalone]);

  const chatQuerySkipped = !enabled || (standalone && !standaloneChat.resumed) || !conversationId || isStarted;

  // network-only: the first result for a conversation is always the server's, so the transcript
  // window and the hook key below are captured from it, never from a cache hit.
  const {
    data: fallbackChatData,
    loading: fallbackLoading,
    refetch: refetchChatData,
  } = useQuery(CHAT_QUERY, {
    variables: { id: conversationId, last: CHAT_PARTS_DEFAULT },
    skip: chatQuerySkipped,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
  });

  // A chat started here never loads CHAT_QUERY; its header and share state come from the cache,
  // filled by the ChatMeta fetch in onFinish (and kept current by updateChatShared's result).
  const { data: metaData } = useQuery(CHAT_META_QUERY, {
    variables: { id: conversationId },
    skip: standalone || !isStarted,
    fetchPolicy: 'cache-only',
  });

  // A chat started in the flyout never loads CHAT_QUERY, so its server-made title is fetched
  // on its own after each answer and read back from the cache here.
  const { data: titleData } = useQuery(CHAT_TITLE_QUERY, {
    variables: { id: conversationId },
    skip: !standalone || !conversationId,
    fetchPolicy: 'cache-only',
  });
  // Same guard as `chatData`: a title left over from the chat being switched away from never shows.
  const fetchedTitle = titleData?.chat?.id === conversationId ? titleData?.chat?.title : undefined;

  // A result still on screen from the chat being left must never render as the one just picked.
  const fetchedChat =
    conversationId && !isStarted && !fallbackLoading && fallbackChatData?.chat?.id === conversationId
      ? fallbackChatData.chat
      : null;

  const [partsWindow, setPartsWindow] = useState<PartsWindow | null>(null);
  const activeWindow = partsWindow?.chatId === conversationId ? partsWindow : null;
  const activeWindowRef = useRef(activeWindow);
  activeWindowRef.current = activeWindow;

  // An opened chat keys useChat at -v0 until its first window lands, then re-keys to -v{length},
  // which discards anything sent in between, so every send path refuses until it lands (the
  // draft stays put). A chat started here skips the query, so it never waits.
  const awaitingWindow = !chatQuerySkipped && fallbackLoading && !activeWindow;

  // The layout-level instance survives /chat/A -> /chat/B, so the window is per conversation.
  useEffect(() => {
    setPartsWindow(null);
  }, [conversationId]);

  useEffect(() => {
    if (!fetchedChat) {
      return;
    }

    setPartsWindow((current) => (current?.chatId === fetchedChat.id ? current : windowFrom(fetchedChat)));
  }, [fetchedChat]);

  const chatData = useMemo(() => {
    if (!conversationId) return null;

    if (isStarted) {
      const meta = metaData?.chat?.id === conversationId ? metaData.chat : null;
      return meta ? { chat: { ...meta, parts: [] } } : null;
    }

    return fetchedChat ? { chat: fetchedChat } : null;
  }, [conversationId, isStarted, metaData, fetchedChat]);

  const initialMessages = !isStarted && fetchedChat?.parts ? partsToMessages(fetchedChat.parts) : [];

  // Generate a consistent chatId for new conversations
  const newChatId = useMemo(() => {
    if (standalone) return standaloneChat.id;
    return conversationId || uuidv7();
  }, [conversationId, standalone, standaloneChat.id]);

  // Frozen once a chat is open: a started chat stays -v0 across the `/chat` -> `/chat/:id`
  // navigation (or the optimistic user message is lost), and an opened chat keeps the length of
  // its first network window, so loading earlier parts never re-keys away this session's turns.
  const chatHookId = `${newChatId}-v${keyLength(isStarted, activeWindow, fetchedChat)}`;

  const [input, setInput] = useState('');
  const pendingSentFilesRef = useRef<ChatFilePart[] | undefined>(undefined);

  // `trigger` is forwarded because a regenerate re-sends a user turn the route has already
  // persisted; without it the route cannot tell a retry from a first send and stores it twice.
  const prepareSendMessagesRequest = useCallback(
    ({ messages, trigger }: { messages: unknown[]; trigger: 'submit-message' | 'regenerate-message' }) => {
      let lastMessage = messages[messages.length - 1] as { role?: string; parts?: unknown[] } | undefined;
      const sentFiles = pendingSentFilesRef.current;

      if (sentFiles?.length && lastMessage) {
        const patched = applyStableFilePartUrls([lastMessage], sentFiles);
        lastMessage = patched[0];
      }

      pendingSentFilesRef.current = undefined;

      if (standalone) {
        return {
          body: {
            targetType,
            targetId,
            userId,
            model,
            chatId: newChatId,
            ...standaloneTurnBody(messages, openingRef.current),
            trigger,
            messages: lastMessage ? [lastMessage] : [],
          },
        };
      }

      return {
        body: {
          targetType,
          targetId,
          userId,
          model,
          chatId: newChatId,
          sparkId: deepLink.sparkId,
          trigger,
          messages: lastMessage ? [lastMessage] : [],
        },
      };
    },
    [targetType, targetId, userId, model, newChatId, deepLink.sparkId, standalone]
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_API_URL.replace('graphql', 'rest')}/chat/chat`,
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          'Content-Type': 'application/json',
        },
        body: {
          targetType,
          targetId,
          userId,
          model,
          chatId: newChatId,
          sparkId: deepLink.sparkId,
        },
        prepareSendMessagesRequest,
        fetch: async (url: RequestInfo | URL, options?: RequestInit) => {
          try {
            return await apiClient.fetch(url, options);
          } catch (error) {
            console.error('Chat error:', error);
            throw error;
          }
        },
      }),
    [token, targetType, targetId, userId, model, newChatId, deepLink.sparkId, prepareSendMessagesRequest, apiClient]
  );

  const chat = useChat({
    id: chatHookId,
    messages: initialMessages,
    transport,
    throttle: 50,
    onError: (error: Error) => {
      // Don't treat 401 errors as fatal since we handle token refresh automatically
      if (
        error?.message?.includes('401') ||
        (error as { status?: number }).status === 401 ||
        error?.message?.includes('Unauthorized')
      ) {
        return;
      }
      console.error('Chat error:', error);
    },
    onFinish: () => {
      const chatId = currentChatIdRef.current;

      if (!standalone && chatId && chatId === startedChatIdRef.current) {
        apolloClient
          .query({ query: CHAT_META_QUERY, variables: { id: chatId }, fetchPolicy: 'network-only' })
          .catch((err) => {
            console.error('Error fetching chat metadata:', err);
          });
      }

      if (standalone && chatId) {
        apolloClient
          .query({ query: CHAT_TITLE_QUERY, variables: { id: chatId }, fetchPolicy: 'network-only' })
          .catch((err) => {
            console.error('Error fetching chat title:', err);
          });
      }

      // Update the sidebar chat history without affecting the current chat view
      if (chatId) {
        apolloClient
          .refetchQueries({
            include: ['MyChats'],
          })
          .catch((err) => {
            console.error('Error refetching chat history:', err);
          });
      }
    },
  });

  const currentChatIdRef = useRef<string | undefined>(conversationId);

  useEffect(() => {
    currentChatIdRef.current = conversationId;
  }, [conversationId]);

  const trackMessage = useCallback(
    (chatId: string, input: string, attachments: number = 0) => {
      const eventData = {
        chat_id: chatId,
        message: input,
        attachments,
      };

      amplitude.track('chat_message_sent', eventData);
      intercom.trackEvent('chat_message_sent', eventData);
    },
    [amplitude, intercom]
  );

  const hasContent = useCallback((input: string, options?: ChatSubmitOptions) => {
    return input.trim() || !!(options?.allowEmptySubmit && options?.files?.length);
  }, []);

  const handleNewChat = useCallback(
    (input: string, options?: ChatSubmitOptions) => {
      trackMessage(newChatId, input, options?.files?.length ?? 0);
      currentChatIdRef.current = newChatId;
      startedChatIdRef.current = newChatId;

      if (standalone) {
        setStandaloneChat((current) => ({ ...current, started: true }));
        return newChatId;
      }

      const newUrl = buildChatUrl(basePath, newChatId);
      navigate(newUrl, { replace: true });

      return newChatId;
    },
    [basePath, navigate, trackMessage, newChatId, standalone]
  );

  const patchSentFileParts = useCallback(
    (files?: ChatFilePart[]) => {
      if (!files?.length) return;
      chat.setMessages((msgs) => applyStableFilePartUrls(msgs, files));
    },
    [chat.setMessages]
  );

  const sendMessageWithFiles = useCallback(
    (
      message: { text: string; files?: ChatFilePart[]; metadata?: ChatTurn },
      options?: { body?: object } & ChatSubmitOptions
    ) => {
      if (awaitingWindow) {
        return undefined;
      }

      const files = message.files;
      pendingSentFilesRef.current = files?.length ? files : undefined;
      const result = chat.sendMessage(
        {
          text: message.text,
          ...(files?.length && { files }),
          ...(message.metadata && { metadata: message.metadata }),
        },
        options ?? {}
      );
      patchSentFileParts(files);
      return result;
    },
    [awaitingWindow, chat.sendMessage, patchSentFileParts]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent, options?: ChatSubmitOptions) => {
      e.preventDefault();
      if (awaitingWindow) {
        return undefined;
      }

      const inputToUse = options?.input !== undefined ? options.input : input;

      if (!hasContent(inputToUse, options)) {
        setInput('');
        return chat.sendMessage({ text: inputToUse ?? '' }, options);
      }

      if (!conversationId) {
        const newChatId = handleNewChat(inputToUse ?? '', options);

        if (options?.input !== undefined) {
          setInput(options.input);
        }
        setInput('');
        return sendMessageWithFiles(
          { text: inputToUse ?? '', files: options?.files },
          { body: { ...options?.body, chatId: newChatId } }
        );
      }

      trackMessage(conversationId, inputToUse ?? '', options?.files?.length ?? 0);

      if (options?.input !== undefined) {
        setInput(options.input);
      }

      setInput('');
      return sendMessageWithFiles({ text: inputToUse ?? '', files: options?.files }, options);
    },
    [
      awaitingWindow,
      conversationId,
      input,
      chat.sendMessage,
      hasContent,
      handleNewChat,
      trackMessage,
      sendMessageWithFiles,
    ]
  );

  const handleAppend = useCallback(
    async (message: { content?: string; [key: string]: unknown }, options?: ChatSubmitOptions) => {
      if (!conversationId) {
        console.warn('Cannot append message without conversation ID');
        return false;
      }
      if (awaitingWindow) {
        return false;
      }

      const content = message.content || '';
      trackMessage(conversationId, content, options?.files?.length ?? 0);

      try {
        await sendMessageWithFiles({ text: content, files: options?.files }, options as { body?: object });
        return true;
      } catch (error) {
        console.error('Failed to append message:', error);
        return false;
      }
    },
    [awaitingWindow, conversationId, trackMessage, sendMessageWithFiles]
  );

  // Standalone only. Returns the fresh chat's id so a caller can wait for the hook to switch to it.
  const startChat = useCallback(() => {
    if (chat.stop) {
      chat.stop();
    }
    const next = freshStandaloneChat();
    setStandaloneChat(next);
    setInput('');
    return next.id;
  }, [chat.stop]);

  // Standalone only: an imperative turn. `turn` rides on this message alone; the draft is untouched.
  const sendText = useCallback(
    async (text: string, turn?: ChatTurn, files?: ChatFilePart[]) => {
      const trimmed = text.trim();
      if (!trimmed && !files?.length) return false;
      if (chat.status === 'submitted' || chat.status === 'streaming') return false;
      if (awaitingWindow) return false;

      if (conversationId) trackMessage(conversationId, trimmed, files?.length ?? 0);
      if (!conversationId) handleNewChat(trimmed, { files });

      await sendMessageWithFiles({ text: trimmed, files, metadata: turnMetadata(turn) });
      return true;
    },
    [awaitingWindow, chat.status, conversationId, handleNewChat, trackMessage, sendMessageWithFiles]
  );

  const newChat = useCallback(() => {
    if (standalone) {
      startChat();
      return;
    }
    if (chat.stop) {
      chat.stop();
    }
    // Clear the last conversation when creating a new chat
    chatHistory.clearLastConversation();
    navigate(basePath, { replace: true });
  }, [navigate, basePath, chat.stop, standalone, startChat]);

  // `started`: a fresh id the caller will send the first message to - nothing to load.
  const navigateToChat = useCallback(
    async (targetConversationId: string, options?: { started?: boolean }) => {
      if (chat.stop) {
        chat.stop();
      }

      if (options?.started) {
        startedChatIdRef.current = targetConversationId;
      }

      if (standalone) {
        setStandaloneChat({ id: targetConversationId, started: true, resumed: true });
        return;
      }

      const url = buildChatUrl(basePath, targetConversationId);
      navigate(url);
    },
    [navigate, basePath, chat.stop, standalone]
  );

  // Apollo's refetch() ignores `skip` and forces the network, so it never runs for a chat this
  // instance started (it would fetch mid-stream) or for a disabled instance.
  const refreshChatData = useCallback(async () => {
    if (!enabled || !conversationId || conversationId === startedChatIdRef.current) {
      return;
    }

    try {
      await refetchChatData();
    } catch (error) {
      console.error('Error refetching chat data:', error);
    }
  }, [enabled, conversationId, refetchChatData]);

  const isLoading = chat.status === 'submitted' || chat.status === 'streaming';

  // Prepends the next older window to useChat state. Never writes the cached CHAT_QUERY result and
  // never changes chatHookId: this session's turns live only in useChat state. Resolves true only
  // when older messages were prepended.
  const loadEarlier = useCallback(async (): Promise<boolean> => {
    const transcript = activeWindowRef.current;
    if (!enabled || isLoading || !transcript || transcript.loading || !hasEarlier(transcript)) {
      return false;
    }

    const { chatId } = transcript;
    const sameChat = (current: PartsWindow | null) => current?.chatId === chatId;
    setPartsWindow((current) => (sameChat(current) ? { ...current!, loading: true } : current));

    try {
      const { data } = await apolloClient.query({
        query: CHAT_QUERY,
        variables: { id: chatId, before: transcript.earliest, last: CHAT_PARTS_DEFAULT },
        fetchPolicy: 'no-cache',
      });
      if (currentChatIdRef.current !== chatId) {
        return false;
      }

      const older = data?.chat?.parts ?? [];
      setPartsWindow((current) => {
        if (!sameChat(current)) {
          return current;
        }

        return {
          ...current!,
          loaded: current!.loaded + older.length,
          earliest: lowestPartIndex(older) ?? current!.earliest,
          exhausted: older.length < CHAT_PARTS_DEFAULT,
        };
      });
      if (!older.length) {
        return false;
      }

      chat.setMessages((messages) => prependMessages(partsToMessages(older), messages));
      return true;
    } catch (error) {
      console.error('Error loading earlier messages:', error);
      return false;
    } finally {
      setPartsWindow((current) => (sameChat(current) ? { ...current!, loading: false } : current));
    }
  }, [enabled, isLoading, apolloClient, chat.setMessages]);

  return {
    ...chat,
    input,
    setInput,
    handleSubmit,
    handleAppend,
    append: handleAppend,
    isLoading,
    reload: chat.regenerate,
    chatId: conversationId,
    chatTitle: chatData?.chat?.title ?? fetchedTitle,
    chatData,
    chatLoading: fallbackLoading,
    awaitingWindow,
    newChat,
    navigateToChat,
    conversationId,
    conversationTitle: chatData?.chat?.title,
    conversationLoading: fallbackLoading,
    navigateToNewConversation: newChat,
    navigateToNewChat: newChat,
    navigateToConversation: navigateToChat,
    refreshChatData,
    pendingMessageTrigger: deepLink.trigger, // For Chat component
    draftChatId: newChatId,
    startChat,
    sendText,
    hasEarlierParts: enabled && hasEarlier(activeWindow),
    loadingEarlier: !!activeWindow?.loading,
    loadEarlier,
  };
}

export const useConversationChat = useChatHook;
